<template>
  <PageDynamic :path="pagePath" :title="pageTitle" :description="pageDescription">

    <h1 v-if="h1Text" class="sr-only">{{ h1Text }}</h1>

    <div v-if="pageContent" v-html="pageContent"></div>

  </PageDynamic>

</template>

<script setup>

import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { usePageContentStore } from "@/stores/pageContent";
import { useShortcodeParser } from "~/composables/useShortcodeParser";
import Spinner from "@/components/helpers/Spinner.vue";
import PageDynamic from "@/components/pages/Dynamic.vue";

const store = usePageContentStore();
const route = useRoute();
const { parseShortcodes } = useShortcodeParser();

// SSR-safe metadata fetch (same pattern as pages/home.vue) — populates
// title/description before render, so SSR HTML has real values instead of
// empty ones. The client-only fetchPageContent below (which also parses
// shortcodes) reuses this same response instead of fetching again.
await store.fetchPageMeta(route.path);

const pageTitle = computed(() => store.title);
const pageDescription = computed(() => store.description);
const isLoading = computed(() => store.loading);
const pagePath = computed(() => store.path || route.path.replace(/^\/(en|th)/, ""));
const pageContent = ref("");

// No reliable per-page title exists from the CMS for these generic pages
// (alternate_title/title is brand-only), so derive a readable H1 from the
// last URL segment instead — synchronous, no extra fetch needed for SSR.
const h1Text = computed(() => {
  const segments = route.path.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] || "";
  return decodeURIComponent(lastSegment)
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
});


const loadScript = (src, isAsync = true, isDefer = true) => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = isAsync;
    script.defer = isDefer;
    script.onload = resolve;
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
};

const injectCustomHead = async (customHead) => {
  const head = document.head;
  const headFragment = document.createRange().createContextualFragment(customHead);
  head.appendChild(headFragment);
};

const injectCustomFooter = async (customFooter) => {
  const body = document.body;
  const footerFragment = document.createRange().createContextualFragment(customFooter);

  // ดึง script จาก customFooter
  const scripts = footerFragment.querySelectorAll('script[src]');
  const scriptUrls = Array.from(scripts).map((script) => script.src);

  // Append footer HTML ยกเว้น script
  scripts.forEach((script) => script.remove());
  body.appendChild(footerFragment);

  // โหลด script ทีละตัว
  for (const src of scriptUrls) {
    try {
      await loadScript(src);
    } catch (error) {
      console.error(`Failed to load script: ${src}`, error);
    }
  }
};

// Fetch the page content based on the route's slug
const fetchPageContent = async () => {
  try {
    // const slug = Array.isArray(route.params.slug)
    //   ? route.params.slug.join("/")
    //   : route.params.slug;

    const path = route.path;
    await store.fetchPageContent(path);
    pageContent.value = store.content;

    if (store.content.custom_head) {
      injectCustomHead(store.content.custom_head);
    }
    if (store.content.custom_footer) {
      injectCustomFooter(store.content.custom_footer);
    }

  } catch (error) {
    console.error("Error fetching page content:", error);
  }
};

onMounted(fetchPageContent);
</script>

<style scoped lang="scss"></style>
